/* eslint-disable @typescript-eslint/no-explicit-any */

import { RequestHeader } from '@/types/base';
import { CryptoService } from './cryptoService';
import { parseJwtPayload } from './jwtClaims';
import { appendStaticFileAuthParams } from './staticFileAuth';

/**
 * Thư viện tiện ích JavaScript phổ biến cho ứng dụng React TypeScript
 * Cung cấp các hàm helper cho các tác vụ thường dùng như format ngày tháng,
 * xử lý chuỗi, thao tác mảng và nhiều tiện ích khác
 */
class CommonJs {
  // ==================== JWT & TOKEN ====================

  /**
   * Giải mã JWT token và trả về payload
   * @param token - JWT token cần giải mã (có thể null)
   * @returns Object payload đã giải mã hoặc null nếu không hợp lệ
   * @example
   * ```typescript
   * const payload = CommonJs.parseJwt('eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9...');
   * ```
   */
  static parseJwt(token: string | null): any {
    return parseJwtPayload(token);
  }

  /**
   * Kiểm tra JWT token đã hết hạn chưa
   * @param token - JWT token cần kiểm tra
   * @returns True nếu token hết hạn hoặc không hợp lệ, false nếu còn hiệu lực
   * @example
   * ```typescript
   * const isExpired = CommonJs.isTokenExpired(userToken);
   * if (isExpired) {
   *   // Chuyển hướng đến trang đăng nhập hoặc làm mới token
   * }
   * ```
   */
  static isTokenExpired(token: string | null): boolean {
    const payload = this.parseJwt(token);
    if (!payload?.exp) return true;
    return Date.now() >= payload.exp * 1000;
  }

  /**
   * Lấy thời gian còn lại trước khi JWT token hết hạn
   * @param token - JWT token cần kiểm tra
   * @returns Thời gian còn lại tính bằng giây, 0 nếu đã hết hạn hoặc không hợp lệ
   * @example
   * ```typescript
   * const remainingSeconds = CommonJs.getTokenRemainingTime(userToken);
   * ```
   */
  static getTokenRemainingTime(token: string | null): number {
    const payload = this.parseJwt(token);
    if (!payload?.exp) return 0;
    const remaining = payload.exp * 1000 - Date.now();
    return Math.max(0, Math.floor(remaining / 1000));
  }

  // ==================== DATE & TIME ====================

  /**
   * Format ngày tháng theo định dạng dd/mm/yyyy
   * @param date - Ngày tháng cần format (Date object hoặc string)
   * @returns Chuỗi ngày tháng theo format dd/mm/yyyy, trả về chuỗi rỗng nếu không hợp lệ
   * @example
   * ```typescript
   * CommonJs.formatDate(new Date()); // "25/08/2024"
   * CommonJs.formatDate('2024-08-25'); // "25/08/2024"
   * ```
   */
  static formatDate(date: Date | string): string {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const day = ('0' + d.getDate()).slice(-2);
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  /**
   * Format ngày giờ theo định dạng dd/mm/yyyy hh:mm
   * @param date - Ngày giờ cần format (Date object hoặc string)
   * @returns Chuỗi ngày giờ theo format dd/mm/yyyy hh:mm
   * @example
   * ```typescript
   * CommonJs.formatDateTime(new Date()); // "25/08/2024 14:30"
   * ```
   */
  static formatDateTime(date: Date | string): string {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const day = ('0' + d.getDate()).slice(-2);
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const year = d.getFullYear();
    const hours = ('0' + d.getHours()).slice(-2);
    const minutes = ('0' + d.getMinutes()).slice(-2);
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  /**
   * Format giờ phút theo định dạng hh:mm
   * @param date - Thời gian cần format (Date object hoặc string)
   * @returns Chuỗi thời gian theo format hh:mm
   * @example
   * ```typescript
   * CommonJs.formatTime(new Date()); // "14:30"
   * ```
   */
  static formatTime(date: Date | string): string {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const hours = ('0' + d.getHours()).slice(-2);
    const minutes = ('0' + d.getMinutes()).slice(-2);
    return `${hours}:${minutes}`;
  }

  /**
   * Tính thời gian đã trôi qua từ một thời điểm (relative time)
   * @param date - Thời điểm trong quá khứ (Date object hoặc string)
   * @returns Chuỗi mô tả thời gian đã trôi qua bằng tiếng Việt
   * @example
   * ```typescript
   * CommonJs.getTimeAgo(new Date(Date.now() - 300000)); // "5 phút trước"
   * CommonJs.getTimeAgo('2024-08-20'); // "5 ngày trước"
   * ```
   */
  static getTimeAgo(date: Date | string): string {
    const now = new Date();
    const past = new Date(date);
    const diffInMs = now.getTime() - past.getTime();
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return 'Vừa xong';
    if (diffInMinutes < 60) return `${diffInMinutes} phút trước`;
    if (diffInHours < 24) return `${diffInHours} giờ trước`;
    if (diffInDays < 30) return `${diffInDays} ngày trước`;

    return this.formatDate(date);
  }

  /**
   * Kiểm tra một ngày có phải là hôm nay không
   * @param date - Ngày cần kiểm tra (Date object hoặc string)
   * @returns True nếu là hôm nay, false nếu không phải
   * @example
   * ```typescript
   * CommonJs.isToday(new Date()); // true
   * CommonJs.isToday('2024-08-24'); // false (nếu hôm nay không phải 24/08/2024)
   * ```
   */
  static isToday(date: Date | string): boolean {
    const today = new Date();
    const checkDate = new Date(date);
    return today.toDateString() === checkDate.toDateString();
  }

  /**
   * Cộng thêm số ngày vào một ngày
   * @param date - Ngày gốc (Date object hoặc string)
   * @param days - Số ngày cần cộng thêm (có thể âm để trừ)
   * @returns Date object mới sau khi cộng thêm số ngày
   * @example
   * ```typescript
   * const tomorrow = CommonJs.addDays(new Date(), 1); // Ngày mai
   * const lastWeek = CommonJs.addDays(new Date(), -7); // Tuần trước
   * ```
   */
  static addDays(date: Date | string, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  // ==================== STRING UTILITIES ====================

  /**
   * Cắt ngắn chuỗi nếu vượt quá độ dài cho phép và thêm "..." vào cuối
   * @param str - Chuỗi cần cắt ngắn
   * @param length - Độ dài tối đa cho phép
   * @returns Chuỗi đã được cắt ngắn với "..." hoặc chuỗi gốc nếu không vượt quá
   * @example
   * ```typescript
   * CommonJs.truncate('Đây là một chuỗi rất dài', 10); // "Đây là mộ..."
   * ```
   */
  static truncate(str: string, length: number): string {
    if (!str) return '';
    return str.length > length ? str.slice(0, length) + '...' : str;
  }

  /**
   * Chuyển chuỗi thành dạng Title Case (viết hoa chữ cái đầu mỗi từ)
   * @param str - Chuỗi cần chuyển đổi
   * @returns Chuỗi đã chuyển thành Title Case
   * @example
   * ```typescript
   * CommonJs.toTitleCase('nguyễn văn a'); // "Nguyễn Văn A"
   * ```
   */
  static toTitleCase(str: string): string {
    if (!str) return '';
    return str.replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
    });
  }

  /**
   * Chuyển chuỗi thành dạng camelCase
   * @param str - Chuỗi cần chuyển đổi
   * @returns Chuỗi đã chuyển thành camelCase
   * @example
   * ```typescript
   * CommonJs.toCamelCase('Tên người dùng'); // "tênNgườiDùng"
   * ```
   */
  static toCamelCase(str: string): string {
    return str
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
        return index === 0 ? word.toLowerCase() : word.toUpperCase();
      })
      .replace(/\s+/g, '');
  }

  /**
   * Chuyển chuỗi thành dạng kebab-case (phân cách bằng dấu gạch ngang)
   * @param str - Chuỗi cần chuyển đổi
   * @returns Chuỗi đã chuyển thành kebab-case
   * @example
   * ```typescript
   * CommonJs.toKebabCase('Tên Người Dùng'); // "tên-người-dùng"
   * ```
   */
  static toKebabCase(str: string): string {
    return str
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .replace(/\s+/g, '-')
      .toLowerCase();
  }

  /**
   * Decode HTML entities trong chuỗi (ví dụ: &amp; thành &, &lt; thành <, &gt; thành >)
   * Safe cho SSR - tự động kiểm tra môi trường browser
   * @param text - Chuỗi chứa HTML entities cần decode
   * @returns Chuỗi đã được decode HTML entities
   * @example
   * ```typescript
   * CommonJs.decodeHtmlEntities('Kiến nghị &amp; Đề xuất'); // "Kiến nghị & Đề xuất"
   * CommonJs.decodeHtmlEntities('&lt;div&gt;'); // "<div>"
   * ```
   */
  static decodeHtmlEntities(text: string): string {
    if (!text) return text;

    // Check if we're in browser environment (safe for SSR)
    if (typeof window === 'undefined') return text;

    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  /**
   * Chuyển chuỗi thành dạng snake_case (phân cách bằng dấu gạch dưới)
   * @param str - Chuỗi cần chuyển đổi
   * @returns Chuỗi đã chuyển thành snake_case
   * @example
   * ```typescript
   * CommonJs.toSnakeCase('Tên Người Dùng'); // "tên_người_dùng"
   * ```
   */
  static toSnakeCase(str: string): string {
    return str
      .replace(/([a-z])([A-Z])/g, '$1_$2')
      .replace(/\s+/g, '_')
      .toLowerCase();
  }

  /**
   * Loại bỏ dấu tiếng Việt trong chuỗi
   * @param str - Chuỗi có dấu tiếng Việt
   * @returns Chuỗi đã loại bỏ dấu
   * @example
   * ```typescript
   * CommonJs.removeAccents('Tiếng Việt'); // "Tieng Viet"
   * ```
   */
  static removeAccents(str: string): string {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  /**
   * Tạo slug từ chuỗi (dùng cho URL thân thiện)
   * @param str - Chuỗi cần tạo slug
   * @returns Slug đã được tạo (không dấu, chữ thường, phân cách bằng dấu gạch ngang)
   * @example
   * ```typescript
   * CommonJs.slug('Bài viết về React JS!'); // "bai-viet-ve-react-js"
   * ```
   */
  static slug(str: string): string {
    return this.removeAccents(str)
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Viết hoa chữ cái đầu tiên của chuỗi
   * @param str - Chuỗi cần viết hoa chữ cái đầu
   * @returns Chuỗi đã viết hoa chữ cái đầu tiên
   * @example
   * ```typescript
   * CommonJs.capitalizeFirst('xin chào'); // "Xin chào"
   * ```
   */
  static capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Trích xuất chữ cái đầu từ họ tên (tối đa 2 ký tự)
   * @param name - Họ tên cần trích xuất
   * @returns Chuỗi chứa các chữ cái đầu (tối đa 2 ký tự)
   * @example
   * ```typescript
   * CommonJs.extractInitials('Nguyễn Văn An'); // "NV"
   * CommonJs.extractInitials('Trần Thị B'); // "TT"
   * ```
   */
  static extractInitials(name: string): string {
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2);
  }

  // ==================== NUMBER & CURRENCY ====================

  /**
   * Format số thành tiền tệ theo định dạng Việt Nam
   * @param amount - Số tiền cần format
   * @param currency - Loại tiền tệ (mặc định là 'VND')
   * @returns Chuỗi tiền tệ đã được format
   * @example
   * ```typescript
   * CommonJs.formatCurrency(1000000); // "1.000.000 ₫"
   * CommonJs.formatCurrency(500, 'USD'); // "$500"
   * ```
   */
  static formatCurrency(amount: number, currency = 'VND'): string {
    if (isNaN(amount)) return '0';
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
    }).format(amount);
  }

  /**
   * Format số theo định dạng Việt Nam (phân cách hàng nghìn bằng dấu chấm)
   * @param num - Số cần format
   * @returns Chuỗi số đã được format
   * @example
   * ```typescript
   * CommonJs.formatNumber(1234567); // "1.234.567"
   * ```
   */
  static formatNumber(num: number): string {
    if (isNaN(num)) return '0';
    return new Intl.NumberFormat('vi-VN').format(num);
  }

  /**
   * Format số tiền với dấu phẩy phân cách hàng nghìn (dùng cho maskmoney)
   * - Nếu số không có phần thập phân: format với dấu phẩy phân cách hàng nghìn
   * - Nếu số có phần thập phân: format phần nguyên với dấu phẩy, giữ dấu chấm thập phân
   * @param num - Số cần format (có thể là number, string, null, undefined)
   * @returns Chuỗi số đã được format với dấu phẩy phân cách hàng nghìn, dấu chấm cho phần thập phân
   * @example
   * ```typescript
   * CommonJs.formatMoney(1234567); // "1,234,567"
   * CommonJs.formatMoney(1234.56); // "1,234.56"
   * CommonJs.formatMoney(1.2); // "1.2"
   * CommonJs.formatMoney('1234567'); // "1,234,567"
   * ```
   */
  static formatMoney(num: number | string | null | undefined, decimals = 3): string {
    if (num === null || num === undefined || num === '') return '';

    // Nếu là string, giữ nguyên để lấy phần thập phân ban đầu
    const originalStr = typeof num === 'string' ? num : String(num);
    // Bỏ dấu phẩy hàng nghìn trước khi parse — parseFloat('2,222') = 2
    const cleanedStr = typeof num === 'string' ? num.replace(/,/g, '').trim() : originalStr;
    const numValue = typeof num === 'string' ? parseFloat(cleanedStr) : num;

    if (isNaN(numValue)) return originalStr;

    // Kiểm tra xem số có phần thập phân không
    const hasDecimal = numValue % 1 !== 0 || (typeof num === 'string' && cleanedStr.includes('.'));

    if (hasDecimal) {
      // Có phần thập phân: làm tròn 3 chữ số sau dấu phẩy, format phần nguyên với dấu phẩy
      const fixedStr = numValue.toFixed(decimals); // đã làm tròn 3 chữ số thập phân
      const parts = fixedStr.split('.');
      const integerPart = parseInt(parts[0], 10);
      const decimalPart = (parts[1] || '').replace(/0+$/, '');

      // Format phần nguyên với dấu phẩy phân cách hàng nghìn
      const formattedInteger = new Intl.NumberFormat('en-US').format(integerPart);

      // Kết hợp phần nguyên đã format và phần thập phân
      return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
    } else {
      // Không có phần thập phân: format với dấu phẩy phân cách hàng nghìn
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(numValue);
    }
  }

  /**
   * Chuyển chuỗi thành số (loại bỏ các ký tự không phải số)
   * @param str - Chuỗi cần chuyển thành số
   * @returns Số đã được chuyển đổi, 0 nếu không thể chuyển
   * @example
   * ```typescript
   * CommonJs.parseNumber('1.234.567 VND'); // 1234567
   * CommonJs.parseNumber('abc123.45'); // 123.45
   * ```
   */
  static parseNumber(str: string): number {
      if (str === undefined || str === null) return 0;

      if (typeof str === 'number') return str;

      if (typeof str === 'string') {
          return parseFloat(str.replace(/[^\d.-]/g, '')) || 0;
      }

      return 0;
  }

  /**
   * Parse string || number thành số có xử lý số thập phân
   * @param str value
   * @param decimals số lẻ
   * @returns 
   */
static parseDecimal(str: string | undefined | number, decimals = 3): number { 
  if (str === undefined || str === null) return 0; 
  let num: number; 
  if (typeof str === 'string') { 
    // bỏ dấu phẩy 
    const clean = str.replace(/,/g, ''); 
    num = parseFloat(clean); 
  } else { 
    num = str; 
  } 
  if (isNaN(num)) return 0; 
  const fixed = parseFloat(num.toFixed(decimals)); 
  // bỏ số lẻ quá nhỏ (ví dụ: 0.0001 -> 0, 0.0005 -> 0.001) 
  if (Math.abs(fixed % 1) < 0.01) { 
    return Math.trunc(fixed); 
  } 
  return fixed; 
}

  /**
   * Parse chuỗi số từ InputNumber và giới hạn số chữ số thập phân.
   *
   * Chức năng:
   * - Loại bỏ dấu phân cách hàng nghìn (",")
   * - Đảm bảo chỉ có 1 dấu thập phân "."
   * - Giới hạn số chữ số phần thập phân theo tham số `decimals`
   * - Trả về number hợp lệ (fallback = 0 nếu không parse được)
   *
   * Ví dụ:
   * - "1.123456"        → 1.123
   * - "100.9999"        → 100.999
   * - "1..23"           → 1.23
   * - "12,500,000.0023" → 12500000.002
   *
   * @param value - Giá trị nhập vào từ InputNumber (string)
   * @param decimals - Số chữ số thập phân tối đa (mặc định = 3)
   * @returns number đã được chuẩn hóa
   */
  static parseNumberWithPrecision(value: string | undefined, decimals = 3): number {
    return this.parseDecimal(value, decimals);
  }

  /**
   * Format dung lượng file thành đơn vị dễ đọc (Bytes, KB, MB, GB, TB)
   * @param bytes - Số byte cần format
   * @param decimals - Số chữ số thập phân (mặc định là 2)
   * @returns Chuỗi dung lượng đã format với đơn vị
   * @example
   * ```typescript
   * CommonJs.formatBytes(1024); // "1 KB"
   * CommonJs.formatBytes(1536, 1); // "1.5 KB"
   * CommonJs.formatBytes(1048576); // "1 MB"
   * ```
   */
  static formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  /**
   * Tính phần trăm từ giá trị và tổng số
   * @param value - Giá trị cần tính phần trăm
   * @param total - Tổng số để tính phần trăm
   * @returns Chuỗi phần trăm đã format (VD: "25%")
   * @example
   * ```typescript
   * CommonJs.formatPercent(25, 100); // "25%"
   * CommonJs.formatPercent(1, 3); // "33%"
   * ```
   */
  // static formatPercent(value: number, total: number): string {
  //   if (total === 0) return '0%';
  //   return `${Math.round((value / total) * 100)}%`;
  // }

    // Helper function to format percentage: 0.00% -> 0%, 12.00% -> 12%, 12.34% -> 12.34%
  static formatPercent(value: number): string {

      if (value === 0 || Math.abs(value) < 0.01) {
          return '0%';
      }
      // Nếu phần thập phân là 0 thì không hiển thị
      const rounded = Math.round(value * 100) / 100;
      if (rounded % 1 === 0) {
          return `${Math.round(rounded)}%`;
      }
      return `${rounded.toFixed(2)}%`;
  };

  // ==================== ARRAY UTILITIES ====================

  /**
   * Loại bỏ các phần tử trùng lặp trong mảng
   * @param arr - Mảng cần loại bỏ trùng lặp
   * @returns Mảng mới đã loại bỏ phần tử trùng lặp
   * @example
   * ```typescript
   * CommonJs.removeDuplicates([1, 2, 2, 3, 3, 3]); // [1, 2, 3]
   * CommonJs.removeDuplicates(['a', 'b', 'a']); // ['a', 'b']
   * ```
   */
  static removeDuplicates<T>(arr: T[]): T[] {
    return [...new Set(arr)];
  }

  /**
   * Nhóm các phần tử trong mảng theo một thuộc tính
   * @param arr - Mảng các object cần nhóm
   * @param key - Thuộc tính để nhóm theo
   * @returns Object chứa các nhóm đã được phân loại
   * @example
   * ```typescript
   * const users = [{name: 'A', role: 'admin'}, {name: 'B', role: 'user'}];
   * CommonJs.groupBy(users, 'role'); // {admin: [{name: 'A', role: 'admin'}], user: [...]}
   * ```
   */
  static groupBy<T>(arr: T[], key: keyof T): Record<string, T[]> {
    return arr.reduce((groups, item) => {
      const group = String(item[key]);
      groups[group] = groups[group] || [];
      groups[group].push(item);
      return groups;
    }, {} as Record<string, T[]>);
  }

  /**
   * Sắp xếp mảng theo một thuộc tính
   * @param arr - Mảng cần sắp xếp
   * @param key - Thuộc tính để sắp xếp theo
   * @param order - Thứ tự sắp xếp ('asc' tăng dần, 'desc' giảm dần)
   * @returns Mảng mới đã được sắp xếp
   * @example
   * ```typescript
   * const users = [{name: 'B', age: 20}, {name: 'A', age: 25}];
   * CommonJs.sortBy(users, 'name'); // Sắp xếp theo tên A->Z
   * CommonJs.sortBy(users, 'age', 'desc'); // Sắp xếp theo tuổi giảm dần
   * ```
   */
  static sortBy<T>(arr: T[], key: keyof T, order: 'asc' | 'desc' = 'asc'): T[] {
    return [...arr].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }

  /**
   * Chia mảng thành các nhóm nhỏ với kích thước cố định
   * @param arr - Mảng cần chia nhỏ
   * @param size - Kích thước mỗi nhóm
   * @returns Mảng 2 chiều chứa các nhóm đã chia
   * @example
   * ```typescript
   * CommonJs.chunk([1, 2, 3, 4, 5], 2); // [[1, 2], [3, 4], [5]]
   * CommonJs.chunk(['a', 'b', 'c', 'd'], 3); // [['a', 'b', 'c'], ['d']]
   * ```
   */
  static chunk<T>(arr: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  }

  // ==================== OBJECT UTILITIES ====================

  static isEmpty = (value: any): boolean => {
    if (value == null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
  };

  /**
   * Tạo bản sao sâu (deep clone) của một object
   * @param obj - Object cần clone
   * @returns Bản sao sâu của object
   * @example
   * ```typescript
   * const original = {a: 1, b: {c: 2}};
   * const cloned = CommonJs.deepClone(original);
   * cloned.b.c = 3; // Không ảnh hưởng đến original
   * ```
   */
  static deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * Loại bỏ các thuộc tính không mong muốn khỏi object
   * @param obj - Object gốc
   * @param keys - Mảng các key cần loại bỏ
   * @returns Object mới đã loại bỏ các thuộc tính không mong muốn
   * @example
   * ```typescript
   * const user = {id: 1, name: 'John', password: '123'};
   * const safeUser = CommonJs.omit(user, ['password']); // {id: 1, name: 'John'}
   * ```
   */
  static omit<T, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> {
    const result = { ...obj };
    keys.forEach((key) => delete result[key]);
    return result;
  }

  /**
   * Chọn lấy chỉ các thuộc tính mong muốn từ object
   * @param obj - Object gốc
   * @param keys - Mảng các key cần giữ lại
   * @returns Object mới chỉ chứa các thuộc tính được chọn
   * @example
   * ```typescript
   * const user = {id: 1, name: 'John', email: 'john@example.com', password: '123'};
   * const publicUser = CommonJs.pick(user, ['id', 'name']); // {id: 1, name: 'John'}
   * ```
   */
  static pick<T extends Record<string, any>, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
    const result = {} as Pick<T, K>;
    keys.forEach((key) => {
      if (key in obj && obj[key] !== undefined) {
        (result as any)[key] = obj[key];
      }
    });
    return result;
  }

  /**
   * Gộp hai object lại với nhau
   * @param obj1 - Object thứ nhất
   * @param obj2 - Object thứ hai (sẽ ghi đè lên obj1 nếu có key trùng)
   * @returns Object mới được gộp từ hai object
   * @example
   * ```typescript
   * const defaults = {theme: 'light', lang: 'vi'};
   * const userPrefs = {theme: 'dark'};
   * const merged = CommonJs.merge(defaults, userPrefs); // {theme: 'dark', lang: 'vi'}
   * ```
   */
  static merge<T extends object, U extends object>(obj1: T, obj2: U): T & U {
    return { ...obj1, ...obj2 };
  }

  // ==================== LOCAL STORAGE ====================

  /**
   * Lưu dữ liệu vào localStorage (tự động stringify)
   * @param key - Key để lưu trữ
   * @param value - Giá trị cần lưu (sẽ được chuyển thành JSON)
   * @example
   * ```typescript
   * CommonJs.setStorage('userSettings', {theme: 'dark', lang: 'vi'});
   * CommonJs.setStorage('lastVisit', new Date());
   * ```
   */
  static setStorage(key: string, value: any): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Lỗi khi lưu vào localStorage:', error);
    }
  }

  /**
   * Lấy dữ liệu từ localStorage (tự động parse)
   * @param key - Key cần lấy
   * @returns Dữ liệu đã được parse hoặc null nếu không tồn tại
   * @example
   * ```typescript
   * const settings = CommonJs.getStorage<UserSettings>('userSettings');
   * const lastVisit = CommonJs.getStorage<string>('lastVisit');
   * ```
   */
  static getStorage<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error('Lỗi khi đọc từ localStorage:', error);
      return null;
    }
  }

  /**
   * Xóa một item khỏi localStorage
   * @param key - Key của item cần xóa
   * @example
   * ```typescript
   * CommonJs.removeStorage('userSettings');
   * ```
   */
  static removeStorage(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Lỗi khi xóa khỏi localStorage:', error);
    }
  }

  /**
   * Xóa toàn bộ dữ liệu trong localStorage
   * @example
   * ```typescript
   * CommonJs.clearStorage(); // Xóa tất cả localStorage
   * ```
   */
  static clearStorage(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Lỗi khi xóa localStorage:', error);
    }
  }

  // ==================== UTILITY FUNCTIONS ====================

  /**
   * Tạo ID ngẫu nhiên với độ dài tùy chọn
   * @param length - Độ dài của ID (mặc định là 8)
   * @returns Chuỗi ID ngẫu nhiên
   * @example
   * ```typescript
   * CommonJs.generateId(); // "aB3dE9fG"
   * CommonJs.generateId(12); // "aB3dE9fGhI1j"
   * ```
   */
  static generateId(length = 8): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Tạo UUID chuẩn (v4) ngẫu nhiên
   * @returns Chuỗi UUID chuẩn v4
   * @example
   * ```typescript
   * CommonJs.generateUUID(); // "550e8400-e29b-41d4-a716-446655440000"
   * ```
   */
  static generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Tạo hàm debounce - trì hoãn thực thi hàm cho đến khi không có gọi mới trong khoảng thời gian nhất định
   * @param func - Hàm cần debounce
   * @param delay - Thời gian trì hoãn tính bằng milliseconds
   * @returns Hàm đã được debounce
   * @example
   * ```typescript
   * const searchDebounced = CommonJs.debounce((query: string) => {
   * }, 500);
   * // Chỉ thực thi sau khi người dùng ngừng gõ 500ms
   * ```
   */
  static debounce<T extends (...args: any[]) => any>(func: T, delay: number): (...args: Parameters<T>) => void {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }

  /**
   * Tạo hàm throttle - giới hạn tần suất thực thi hàm
   * @param func - Hàm cần throttle
   * @param delay - Khoảng cách tối thiểu giữa các lần thực thi (milliseconds)
   * @returns Hàm đã được throttle
   * @example
   * ```typescript
   * const handleScrollThrottled = CommonJs.throttle(() => {
   * }, 100);
   * // Chỉ thực thi tối đa mỗi 100ms một lần
   * ```
   */
  static throttle<T extends (...args: any[]) => any>(func: T, delay: number): (...args: Parameters<T>) => void {
    let lastCall = 0;
    return (...args: Parameters<T>) => {
      const now = Date.now();
      if (now - lastCall >= delay) {
        lastCall = now;
        func(...args);
      }
    };
  }

  /**
   * Tạm dừng thực thi trong một khoảng thời gian (dùng với async/await)
   * @param ms - Thời gian tạm dừng tính bằng milliseconds
   * @returns Promise sẽ resolve sau khoảng thời gian chỉ định
   * @example
   * ```typescript
   * await CommonJs.sleep(2000); // Tạm dừng 2 giây
   * ```
   */
  static sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Tạo số ngẫu nhiên trong khoảng min-max
   * @param min - Số nhỏ nhất
   * @param max - Số lớn nhất
   * @returns Số ngẫu nhiên trong khoảng [min, max]
   * @example
   * ```typescript
   * CommonJs.random(1, 10); // Số ngẫu nhiên từ 1 đến 10
   * CommonJs.random(100, 200); // Số ngẫu nhiên từ 100 đến 200
   * ```
   */
  static random(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  /**
   * Tạo mã màu hex ngẫu nhiên
   * @returns Chuỗi mã màu hex (VD: "#ff5733")
   * @example
   * ```typescript
   * CommonJs.randomColor(); // "#a3c2f1"
   * CommonJs.randomColor(); // "#ff5733"
   * ```
   */
  static randomColor(): string {
    return '#' + Math.floor(Math.random() * 16777215).toString(16);
  }

  // ==================== FILE & URL ====================

  /**
   * Tải xuống file từ nội dung text
   * @param content - Nội dung của file
   * @param filename - Tên file sẽ được tải xuống
   * @param contentType - Loại nội dung (MIME type)
   * @example
   * ```typescript
   * CommonJs.downloadFile('Hello World', 'greeting.txt', 'text/plain');
   * CommonJs.downloadFile(JSON.stringify(data), 'data.json', 'application/json');
   * ```
   */
    static downloadFile(content: string, filename: string, contentType = 'text/plain'): void {

    const isUrl = /^https?:\/\//i.test(content);

    if (isUrl) {
        content = appendStaticFileAuthParams(content);
        if (content.includes('/file/static/')) {
            content += content.includes('?') ? '&isDownload=true' : '?isDownload=true';
        }
        // 👉 Trường hợp content là URL
        const link = document.createElement('a');
        link.href = content;
        link.download = filename || ''; // có thể bị ignore nếu cross-origin
        link.target = '_blank';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
    }

    const blob = new Blob([content], { type: contentType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    }

    static makeLinkDownload(url: string) {
        url = appendStaticFileAuthParams(url);
        if (url.includes('/file/static/')) {
            url += url.includes('?') ? '&isDownload=true' : '?isDownload=true';
        }
        return url;
    }

    static makeLinkPreviewOffice(url: string) {
        url = appendStaticFileAuthParams(url);
        if (url.includes('/api/file/static/')) {
            url += url.includes('?') ? '&isDownload=true' : '?isDownload=true';
        }
        if (url.includes('https://finder-topkpi.toppion.com.vn:8443/')) {
            url = url.replaceAll('https://finder-topkpi.toppion.com.vn:8443/', 'https://finder-topkpi.toppion.com.vn/');
        }
        return url;
    }

  /**
   * Sao chép text vào clipboard (bộ nhớ tạm)
   * @param text - Text cần sao chép
   * @returns Promise hoàn thành khi sao chép thành công
   * @example
   * ```typescript
   * await CommonJs.copyToClipboard('Hello World');
   * // Text đã được sao chép vào clipboard
   * ```
   */
  static copyToClipboard(text: string): Promise<void> {
    if (navigator.clipboard) {
      return navigator.clipboard.writeText(text);
    } else {
      // Fallback cho trình duyệt cũ
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return Promise.resolve();
    }
  }

  /**
   * Lấy phần mở rộng của file từ tên file
   * @param filename - Tên file cần lấy extension
   * @returns Phần mở rộng của file (không bao gồm dấu chấm)
   * @example
   * ```typescript
   * CommonJs.getFileExtension('document.pdf'); // "pdf"
   * CommonJs.getFileExtension('image.jpg'); // "jpg"
   * ```
   */
  static getFileExtension(filename: string): string {
    return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
  }

  /**
   * Kiểm tra file có phải là file hình ảnh không
   * @param filename - Tên file cần kiểm tra
   * @returns True nếu là file hình ảnh, false nếu không
   * @example
   * ```typescript
   * CommonJs.isImageFile('photo.jpg'); // true
   * CommonJs.isImageFile('document.pdf'); // false
   * ```
   */
  static isImageFile(filename: string): boolean {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    return imageExtensions.includes(this.getFileExtension(filename).toLowerCase());
  }

  // ==================== BROWSER & DEVICE ====================

  /**
   * Kiểm tra có phải đang sử dụng thiết bị di động không
   * @returns True nếu là mobile device, false nếu là desktop
   * @example
   * ```typescript
   * if (CommonJs.isMobile()) {
   *   // Hiển thị giao diện mobile
   * }
   * ```
   */
  static isMobile(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  /**
   * Kiểm tra có phải đang sử dụng thiết bị iOS không
   * @returns True nếu là iOS device (iPhone, iPad, iPod)
   * @example
   * ```typescript
   * if (CommonJs.isIOS()) {
   *   // Xử lý đặc biệt cho iOS
   * }
   * ```
   */
  static isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  /**
   * Kiểm tra có phải đang sử dụng thiết bị Android không
   * @returns True nếu là Android device
   * @example
   * ```typescript
   * if (CommonJs.isAndroid()) {
   *   // Xử lý đặc biệt cho Android
   * }
   * ```
   */
  static isAndroid(): boolean {
    return /Android/.test(navigator.userAgent);
  }

  /**
   * Lấy tên trình duyệt đang sử dụng
   * @returns Tên trình duyệt (Chrome, Firefox, Safari, Edge, Opera, hoặc Unknown)
   * @example
   * ```typescript
   * const browser = CommonJs.getBrowserName(); // "Chrome"
   * ```
   */
  static getBrowserName(): string {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    if (userAgent.includes('Opera')) return 'Opera';
    return 'Unknown';
  }

  // ==================== VIETNAMESE SPECIFIC ====================

  /**
   * Loại bỏ dấu tiếng Việt một cách chi tiết (bao gồm tất cả ký tự có dấu)
   * @param str - Chuỗi tiếng Việt có dấu
   * @returns Chuỗi đã loại bỏ dấu tiếng Việt
   * @example
   * ```typescript
   * CommonJs.removeVietnameseTones('Xin chào Việt Nam!'); // "Xin chao Viet Nam!"
   * CommonJs.removeVietnameseTones('Đăng ký tài khoản'); // "Dang ky tai khoan"
   * ```
   */
  static removeVietnameseTones(str: string): string {
    return str
      .replace(/[àáạảãâầấậẩẫăằắặẳẵ]/g, 'a')
      .replace(/[èéẹẻẽêềếệểễ]/g, 'e')
      .replace(/[ìíịỉĩ]/g, 'i')
      .replace(/[òóọỏõôồốộổỗơờớợởỡ]/g, 'o')
      .replace(/[ùúụủũưừứựửữ]/g, 'u')
      .replace(/[ỳýỵỷỹ]/g, 'y')
      .replace(/đ/g, 'd')
      .replace(/[ÀÁẠẢÃÂẦẤẬẨẪĂẰẮẶẲẴ]/g, 'A')
      .replace(/[ÈÉẸẺẼÊỀẾỆỂỄ]/g, 'E')
      .replace(/[ÌÍỊỈĨ]/g, 'I')
      .replace(/[ÒÓỌỎÕÔỒỐỘỔỖƠỜỚỢỞỠ]/g, 'O')
      .replace(/[ÙÚỤỦŨƯỪỨỰỬỮ]/g, 'U')
      .replace(/[ỲÝỴỶỸ]/g, 'Y')
      .replace(/Đ/g, 'D');
  }

  /**
   * Format số điện thoại Việt Nam theo chuẩn quốc tế
   * @param phone - Số điện thoại cần format
   * @returns Số điện thoại đã format theo chuẩn (+84...)
   * @example
   * ```typescript
   * CommonJs.formatPhoneVN('0901234567'); // "+84901234567"
   * CommonJs.formatPhoneVN('84901234567'); // "+84901234567"
   * CommonJs.formatPhoneVN('+84901234567'); // "+84901234567"
   * ```
   */
  static formatPhoneVN(phone: string): string {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('84')) {
      return `+${cleaned}`;
    } else if (cleaned.startsWith('0')) {
      return `+84${cleaned.slice(1)}`;
    }
    return phone;
  }

  /**
   * Format số CMND/CCCD Việt Nam với khoảng cách phù hợp
   * @param id - Số CMND/CCCD cần format
   * @returns Số CMND/CCCD đã format với khoảng cách
   * @example
   * ```typescript
   * CommonJs.formatIDCardVN('123456789'); // "123 456 789" (CMND 9 số)
   * CommonJs.formatIDCardVN('123456789012'); // "123 456 789 012" (CCCD 12 số)
   * ```
   */
  static formatIDCardVN(id: string): string {
    const cleaned = id.replace(/\D/g, '');
    if (cleaned.length === 9) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
    } else if (cleaned.length === 12) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{3})/, '$1 $2 $3 $4');
    }
    return id;
  }

  static convertObjectToArray(obj: any): any[] {
    if (typeof obj === 'object' && obj !== null) {
      return Object.values(obj) as any[];
    } else if (Array.isArray(obj)) {
      return obj;
    }
    return [obj];
  }
  /**
   * Format ngày tháng theo định dạng dd/mm (chỉ ngày và tháng, không có năm)
   * @param dateStr - Ngày tháng cần format (Date object hoặc string)
   * @returns Chuỗi ngày tháng theo format dd/mm
   * @example
   * ```typescript
   * CommonJs.formatDateDDMM('2025-07-01'); // "01/07"
   * ```
   */
  static formatDateDDMM(dateStr: string | Date): string {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
  }

  /**
   * Tạo màu hex từ tên (dùng để tạo màu nền cho avatar khi không có hình ảnh)
   * @param name - Tên cần tạo màu
   * @returns Mã màu hex (VD: "#A3C2F1")
   * @example
   * ```typescript
   * CommonJs.genColorByName('Nguyễn Văn A'); // "#A3C2F1"
   * CommonJs.genColorByName('Trần Thị B'); // "#B4D3E2"
   * ```
   */
  static genColorByName(name: string): string {
    if (!name || name.trim() === '') {
      return '#808080'; // Màu xám mặc định nếu không có tên
    }

    let hash = 0;

    // Tính toán hash từ chuỗi tên
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';

    // Tạo màu từ hash
    for (let i = 0; i < 3; i++) {
      let value = (hash >> (i * 8)) & 0xff;
      // Điều chỉnh giá trị màu để màu sắc nhẹ nhàng hơn
      value = Math.floor(value * 0.7 + 60); // Giảm độ tươi bằng cách giảm hệ số và tăng giá trị cơ bản
      color += value.toString(16).padStart(2, '0').toUpperCase(); // Chuyển đổi thành mã màu hex và thêm vào chuỗi
    }

    return color;
  }
  // Format label: nếu có hơn 3 chữ thì cắt xuống dòng
  static formatLabel = (label: string): string => {
    if (!label) return '';
    const words = label.trim().split(/\s+/);
    if (words.length > 3) {
      // Cắt ở giữa: lấy nửa đầu và nửa sau
      const midPoint = Math.ceil(words.length / 2);
      const firstLine = words.slice(0, midPoint).join(' ');
      const secondLine = words.slice(midPoint).join(' ');
      return `${firstLine}\n${secondLine}`;
    }
    return label;
  };
  static checkNullIDSelectRequestHeader(requestHeader: RequestHeader): boolean {
    return !String(requestHeader?.selectTree?.idSelected ?? '').trim();
  };
  static checkNullGiaiDoanRequestHeader(requestHeader: RequestHeader): boolean {
    return !String(requestHeader?.selectPeriod?.idGiaiDoan ?? '').trim();
  };
  static checkIsEmployee(id: string): boolean {
    if (!id || typeof id !== 'string') return false;
    return id.endsWith('_nv');
  }
  static getEmpIDFromEmpDepKey(value: string): string {
    if (!value || typeof value !== 'string') return '';
    return value.replace(/^emp_(.+)_dep_.+$/, '$1');
  }
  // Format number
  static formatPercentNumber(value: number | string | undefined | null): string {
    if (value === undefined || value === null || value === '') return '';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return String(value);
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };
  static createTempID(): string {
    return `temp${CryptoService.generateRandomIV()}`;
  }

  /**
   * Lighten a hex color
   * @param color - The hex color to lighten
   * @param percent - The percentage to lighten the color
   * @returns The lightened hex color
   */
  static lightenColor = (color: string, percent: number): string => {
    if (!color) return color;
    let r: number, g: number, b: number;
    if (color.startsWith('#')) {
      const num = parseInt(color.replace('#', ''), 16);
      r = (num >> 16) & 0xff;
      g = (num >> 8) & 0xff;
      b = num & 0xff;
    } else if (color.startsWith('rgb')) {
      // rgb hoặc rgba
      const match = color.match(/\d+(\.\d+)?/g);
      if (!match || match.length < 3) return color;
      r = Number(match[0]);
      g = Number(match[1]);
      b = Number(match[2]);
    } else {
      // format khác thì trả lại nguyên bản
      return color;
    }
    const newR = Math.min(255, Math.round(r + (255 - r) * (percent / 100)));
    const newG = Math.min(255, Math.round(g + (255 - g) * (percent / 100)));
    const newB = Math.min(255, Math.round(b + (255 - b) * (percent / 100)));
    return `#${((newR << 16) | (newG << 8) | newB).toString(16).padStart(6, '0')}`;
  };
  
  //#region TINH TOAN CHI TIEU

  static calLastValue(hinhThucTinh: number, valueChiTieu: number, valueSingle: number, totalMonths: number): number {
    let lastValue = valueSingle;
    if(hinhThucTinh === 1) { //tổng
      const totalValue = valueSingle * (totalMonths - 1);
      lastValue = CommonJs.parseDecimal(valueChiTieu - totalValue, 3);
      console.log('totalValue', valueSingle, totalMonths, totalValue, lastValue);
    }else { //trung bình || tiến độ  
        lastValue = CommonJs.parseDecimal(valueChiTieu, 3);
    }
    return lastValue;
  }

  //#endregion

  /**
   * Lấy danh sách các tháng bắt đầu của các kỳ
   * @param frequencyVal - Giá trị của hình thức tính
   * @returns Danh sách các tháng bắt đầu của các kỳ
   * @example
   * ```typescript
   * CommonJs.getPeriodStarts(3); // [1, 4, 7, 10]
   * CommonJs.getPeriodStarts(4); // [1, 7]
   * CommonJs.getPeriodStarts(5); // [1]
   * CommonJs.getPeriodStarts(2); // [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
   * ```
   */
  static getPeriodStarts(frequencyVal: number): number[] {
    if (frequencyVal === 3) return [1, 4, 7, 10]; // Quý
    if (frequencyVal === 4) return [1, 7];        // 6 tháng
    if (frequencyVal === 5) return [1];           // Năm
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]; // Tháng
  }
}

export default CommonJs;
